import {
  alignmentPlugin,
  dirtyPlugin,
  FabricEditor,
  FabricEditorProvider,
  fillGradientPlugin,
  fontLoadingPlugin,
  gridPlugin,
  historyPlugin,
  hotkeyPlugin,
  layerPlugin,
  rectCornersPlugin,
  resizeNormalizePlugin,
  selectionPlugin,
  zoomPlugin,
} from 'modules/fabric-editor';
import { layoutBlocksPlugin } from 'modules/manager/domain/layout-blocks';
import { logoPlugin } from 'modules/manager/domain/logo';
import { placeholderPlugin } from 'modules/manager/domain/placeholder';
import {
  EditorCanvas,
  EditorHeader,
  EditorLayout,
  EditorLeftToolbar,
  EditorRightToolbar,
  UnsavedChangesGuard,
} from 'modules/manager/ui/components/editor';
import {
  StyleClipboardProvider,
  useLoadTemplate,
  useRestoreTemplate,
  useStyleClipboardHotkeys,
} from 'modules/manager/ui/hooks';
import { selectActiveTemplate } from 'modules/manager/store';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from 'store/hooks';

const PLUGINS = [
  historyPlugin(),
  zoomPlugin(),
  selectionPlugin(),
  alignmentPlugin(),
  layerPlugin(),
  gridPlugin(),
  hotkeyPlugin(),
  dirtyPlugin(),
  resizeNormalizePlugin(),
  rectCornersPlugin(),
  fillGradientPlugin(),
  fontLoadingPlugin(),
  placeholderPlugin(),
  layoutBlocksPlugin(),
  logoPlugin(),
];

const MediaManagerEditorPage: React.FC = () => {
  const { isLoaded } = useLoadTemplate();
  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const template = useAppSelector(selectActiveTemplate);
  useRestoreTemplate(editor);

  useEffect(() => {
    if (!editor?.placeholders || !template) return;
    editor.placeholders.setTemplateType?.(template.templateType);
  }, [editor, template?.templateType]);

  useEffect(() => {
    if (!editor?.hotkeys || !editor.layers) return;
    const h = editor.hotkeys;
    const l = editor.layers;
    const bindings: Array<[string, () => void]> = [
      ['ctrl+]', () => l.bringForward()],
      ['meta+]', () => l.bringForward()],
      ['ctrl+shift+]', () => l.bringToFront()],
      ['meta+shift+]', () => l.bringToFront()],
      ['ctrl+[', () => l.sendBackwards()],
      ['meta+[', () => l.sendBackwards()],
      ['ctrl+shift+[', () => l.sendToBack()],
      ['meta+shift+[', () => l.sendToBack()],
    ];
    const a = editor.alignment;
    if (a) {
      bindings.push(
        ['alt+a', () => a.alignLeft()],
        ['alt+d', () => a.alignRight()],
        ['alt+w', () => a.alignTop()],
        ['alt+s', () => a.alignBottom()],
        ['alt+h', () => a.distributeHorizontally()],
        ['alt+v', () => a.distributeVertically()],
      );
    }
    for (const [combo, fn] of bindings) h.bind(combo, fn);
    return () => {
      for (const [combo] of bindings) h.unbind(combo);
    };
  }, [editor]);

  return (
    <FabricEditorProvider editor={editor}>
      <StyleClipboardProvider>
        <StyleClipboardHotkeys editor={editor} />
        <EditorLayout
          header={<EditorHeader />}
          left={editor ? <EditorLeftToolbar /> : null}
          right={editor ? <EditorRightToolbar /> : null}
          canvas={<EditorCanvas plugins={PLUGINS} onReady={setEditor} />}
          isLoading={!isLoaded}
        />
        <UnsavedChangesGuard editor={editor} />
      </StyleClipboardProvider>
    </FabricEditorProvider>
  );
};

interface StyleClipboardHotkeysProps {
  editor: FabricEditor | null;
}

// Render-less bridge: binds the style-clipboard hotkeys inside the provider so
// they share the clipboard context with the right-toolbar section.
const StyleClipboardHotkeys: React.FC<StyleClipboardHotkeysProps> = ({ editor }) => {
  useStyleClipboardHotkeys(editor);
  return null;
};

export default MediaManagerEditorPage;
